import { useState, useEffect, useRef } from "react";
/* eslint-disable react-hooks/exhaustive-deps -- user loaders are intentionally keyed by active branch */
import { supabase, type Usuario } from "../../lib/supabase";
import { useSucursal } from "../../utils/sucursalContext";
import AdminLayout from "../../components/layout/AdminLayout";
import { useAuth } from "../../contexts/AuthContext";
import { calcularRiesgosFuga, type RiesgoFuga } from "../../utils/riesgoFuga";
import { canvasToCameraJpeg } from "../../utils/facialImage";

type Plan = {
  id: string;
  nombre: string;
  descripcion: string | null;
  precio: number;
  duracion_dias: number;
  max_dias_semana?: number | null;
  max_sesiones_periodo?: number | null;
  activo?: boolean | null;
  color?: string | null;
  permanencia_meses?: number | null;
};

type BiometriaFacialEstado = "enrolado" | "pendiente_validacion" | "rechazado";

type BiometriaFacialRow = {
  usuario_id: string;
  estado: BiometriaFacialEstado;
  updated_at: string;
};

type SyncSummary = {
  total: number;
  sincronizadas: number;
  pendientes: number;
  errores: number;
  vinculados: string[];
  idsPendientesVinculo: number[];
};

type CoachExterno = {
  id: string;
  nombre: string;
  email: string;
  sucursal_id: string | null;
};
type ImportRow = {
  fila: number;
  nombre: string;
  email: string;
  telefono: string;
  fecha_nacimiento: string;
  plan: string;
  estado: string;
  errores: string[];
};
type ImportFailure = { fila: number; email: string; detalle: string };
type RenovacionAutomatica = {
  usuario_id: string;
  proveedor: "mercadopago" | "transbank_oneclick_mall" | "fintoc" | "flow";
  estado: string;
  proximo_cobro_at?: string | null;
};
type TerminationQuote = {
  permanencia_meses: number;
  mensualidades_cumplidas: number;
  mensualidades_restantes: number;
  valor_mensual: number;
  porcentaje_cargo: number;
  cargo_monto: number;
  aplica_cargo: boolean;
};
type PendingTermination = TerminationQuote & { id: string; usuario_id: string; liquidacion_estado: string };

function formatUnknownError(err: unknown, fallback: string) {
  if (!err) return fallback;
  if (err instanceof Error) return err.message || fallback;
  if (typeof err === "string") return err;

  const anyErr = err as any;
  const msg = typeof anyErr?.message === "string" ? anyErr.message : "";
  const details = typeof anyErr?.details === "string" ? anyErr.details : "";
  const hint = typeof anyErr?.hint === "string" ? anyErr.hint : "";
  const code = typeof anyErr?.code === "string" ? anyErr.code : "";

  const composed = [msg, details, hint].filter(Boolean).join(" - ");

  // Friendly mapping for common Postgres errors.
  const lc = (msg + " " + details + " " + hint).toLowerCase();
  if (lc.includes("coach_sin_plan_activo")) {
    return "El coach seleccionado no tiene un plan activo y vigente para esta sucursal.";
  }
  if (lc.includes("limite_clientes_coach_alcanzado")) {
    const limit = lc.match(/limite_clientes_coach_alcanzado:(\d+)/)?.[1];
    return limit
      ? `El coach ya alcanzó el máximo de ${limit} clientes permitido por su plan.`
      : "El coach ya alcanzó el máximo de clientes permitido por su plan.";
  }
  if (
    code === "23505" ||
    lc.includes("duplicate key") ||
    lc.includes("unique constraint")
  ) {
    if (lc.includes("email")) return "El email ya está registrado.";
    return "Ya existe un registro con esos datos.";
  }
  if (
    lc.includes("inscripción tiene actividad") ||
    lc.includes("inscripcion tiene actividad")
  ) {
    return "Esta inscripción ya tiene pagos o actividad registrada y no puede liberarse. Puedes dejar al usuario inactivo.";
  }
  if (
    lc.includes("usuario tiene pagos") ||
    lc.includes("usuario ya tiene actividad")
  ) {
    return "Este usuario ya tiene pagos o actividad registrada. Déjalo inactivo para conservar su historial.";
  }
  if (
    code === "23503" ||
    lc.includes("foreign key") ||
    lc.includes("still referenced")
  ) {
    return "Este usuario tiene información asociada y no puede eliminarse directamente. Si no completó su inscripción, utiliza “Liberar inscripción”; de lo contrario, déjalo inactivo para conservar su historial.";
  }
  if (
    code === "42501" ||
    lc.includes("not authorized") ||
    lc.includes("no autorizado")
  ) {
    return "No tienes permisos para realizar esta acción.";
  }

  // PostgREST/Postgres details are logged for support, never shown to staff.
  if (code) return fallback;

  return composed || code || fallback;
}

function formatISODateLocal(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseISODateLocal(iso: string) {
  const parts = iso.split("-").map((v) => Number(v));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d);
}

function getContractProgress(usuario: Usuario) {
  const total = Number(usuario.permanencia_meses_contratada || 0);
  if (![6, 12].includes(total) || !usuario.fecha_inicio) return null;
  const start = parseISODateLocal(usuario.fecha_inicio);
  if (!start) return null;
  const now = new Date();
  let elapsed =
    (now.getFullYear() - start.getFullYear()) * 12 +
    (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) elapsed -= 1;
  const current = Math.min(total, Math.max(1, elapsed + 1));
  return { current, total, completed: elapsed >= total };
}

export default function UsuariosPage() {
  const { sucursalActiva } = useSucursal();
  const { profile } = useAuth();
  const canTransferUsers = ["platform_admin", "gym_admin", "admin"].includes(
    profile?.role || "",
  );
  const canManageSubscriptions = [
    "platform_admin",
    "gym_admin",
    "admin",
  ].includes(profile?.role || "");
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [planes, setPlanes] = useState<Plan[]>([]);
  const [coachesExternos, setCoachesExternos] = useState<CoachExterno[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEstado, setFilterEstado] = useState<string>("todos");
  const [filterRiesgo, setFilterRiesgo] = useState<string>("todos");
  const [riesgosByUsuario, setRiesgosByUsuario] = useState<
    Record<string, RiesgoFuga>
  >({});
  const [selectedRiesgo, setSelectedRiesgo] = useState<RiesgoFuga | null>(null);
  const [showImportUsers, setShowImportUsers] = useState(false);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [importingUsers, setImportingUsers] = useState(false);
  const [importSummary, setImportSummary] = useState<string | null>(null);
  const [importFinished, setImportFinished] = useState(false);
  const [importFailures, setImportFailures] = useState<ImportFailure[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingUser, setEditingUser] = useState<Usuario | null>(null);
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [renovacionesByUsuario, setRenovacionesByUsuario] = useState<
    Record<string, RenovacionAutomatica>
  >({});
  const [subscriptionUser, setSubscriptionUser] = useState<Usuario | null>(
    null,
  );
  const [cancelReason, setCancelReason] = useState("Solicitud del cliente");
  const [cancelNotes, setCancelNotes] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelResult, setCancelResult] = useState<string | null>(null);
  const [cancelType, setCancelType] = useState<"renewal" | "termination">("renewal");
  const [terminationQuote, setTerminationQuote] = useState<TerminationQuote | null>(null);
  const [terminationException, setTerminationException] = useState("ninguna");
  const [terminationExceptionDetail, setTerminationExceptionDetail] = useState("");
  const [terminationCancellationId, setTerminationCancellationId] = useState<string | null>(null);
  const [terminationPaymentMethod, setTerminationPaymentMethod] = useState("efectivo");
  const [terminationPaymentReference, setTerminationPaymentReference] = useState("");
  const [pendingTerminations, setPendingTerminations] = useState<Record<string, PendingTermination>>({});
  const [faceCaptureDataUrl, setFaceCaptureDataUrl] = useState("");
  const [faceCaptureOpen, setFaceCaptureOpen] = useState(false);
  const [faceCaptureBusy, setFaceCaptureBusy] = useState(false);
  const [faceCaptureError, setFaceCaptureError] = useState<string | null>(null);
  const [biometriaFacialByUsuario, setBiometriaFacialByUsuario] = useState<
    Record<string, BiometriaFacialRow>
  >({});
  const [syncByUsuario, setSyncByUsuario] = useState<
    Record<string, SyncSummary>
  >({});
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    whatsapp: "",
    fecha_nacimiento: "",
    tipo_membresia: "",
    tipo_cliente: "socio_gimnasio" as "socio_gimnasio" | "cliente_externo",
    profesor_admin_id: "",
    fecha_inicio: "",
    fecha_vencimiento: "",
    permanencia_meses_contratada: 12 as 6 | 12,
    pago_exento: false,
    pago_exento_motivo: "",
    pago_exento_hasta: "",
    estado: "activo" as "activo" | "inactivo" | "vencido",
    requiere_captura_facial: false,
    notificar_email: true,
    notificar_whatsapp: true,
  });

  const calcularVencimientoPorPlan = (
    planNombre: string,
    fechaInicioISO: string,
    fallback: string,
  ) => {
    const plan = planes.find((p) => p.nombre === planNombre);
    if (!plan || !plan.duracion_dias || plan.duracion_dias <= 0)
      return fallback;
    const base = parseISODateLocal(fechaInicioISO);
    if (!base) return fallback;
    base.setDate(base.getDate() + plan.duracion_dias);
    return formatISODateLocal(base);
  };

  const downloadUsersFile = (
    rows: Record<string, unknown>[],
    fileName: string,
  ) => {
    const headers = Object.keys(rows[0] || {});
    const escape = (value: unknown) =>
      `"${String(value ?? "").replaceAll('"', '""')}"`;
    const csv = `\uFEFF${[
      headers,
      ...rows.map((row) => headers.map((header) => row[header])),
    ]
      .map((row) => row.map(escape).join(";"))
      .join("\r\n")}`;
    const href = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(href);
  };

  const exportUsers = () => {
    if (!canTransferUsers) return;
    downloadUsersFile(
      usuarios.map((u) => ({
        nombre: u.nombre,
        email: u.email,
        telefono: u.telefono || "",
        whatsapp: u.whatsapp || "",
        fecha_nacimiento: u.fecha_nacimiento || "",
        plan: u.tipo_membresia || "",
        estado: u.estado,
        fecha_inicio: u.fecha_inicio || "",
        fecha_vencimiento: u.fecha_vencimiento || "",
      })),
      `clientes-${sucursalActiva?.nombre || "sucursal"}-${formatISODateLocal(new Date())}.csv`,
    );
  };

  const downloadImportTemplate = () =>
    downloadUsersFile(
      [
        {
          nombre: "Nombre Apellido",
          email: "cliente@correo.cl",
          telefono: "+56912345678",
          fecha_nacimiento: "1990-01-31",
          plan: planes[0]?.nombre || "",
          estado: "activo",
        },
      ],
      "plantilla-importacion-clientes.csv",
    );

  const parseImportFile = async (file: File) => {
    setImportSummary(null);
    setImportFinished(false);
    setImportFailures([]);
    let raw: Record<string, unknown>[];
    try {
      const text = (await file.text()).replace(/^\uFEFF/, "");
      const parseLine = (line: string) => {
        const values: string[] = [];
        let current = "";
        let quoted = false;
        for (let index = 0; index < line.length; index += 1) {
          const character = line[index];
          if (character === '"' && quoted && line[index + 1] === '"') {
            current += '"';
            index += 1;
          } else if (character === '"') quoted = !quoted;
          else if (character === ";" && !quoted) {
            values.push(current);
            current = "";
          } else current += character;
        }
        values.push(current);
        return values;
      };
      const lines = text.split(/\r?\n/).filter((line) => line.trim());
      const headers = parseLine(lines.shift() || "").map((value) =>
        value.trim(),
      );
      if (!headers.length || !headers.includes("email"))
        throw new Error("El CSV no contiene los encabezados esperados.");
      raw = lines.map((line) =>
        Object.fromEntries(
          headers.map((header, index) => [
            header,
            parseLine(line)[index] || "",
          ]),
        ),
      );
    } catch (err) {
      setImportRows([]);
      setImportSummary(
        formatUnknownError(err, "No se pudo leer el archivo seleccionado."),
      );
      return;
    }
    const existing = new Set(usuarios.map((u) => u.email.trim().toLowerCase()));
    const seen = new Set<string>();
    const planNames = new Set(planes.map((p) => p.nombre.trim().toLowerCase()));
    setImportRows(
      raw.map((row, index) => {
        const nombre = String(row.nombre || "").trim();
        const email = String(row.email || "")
          .trim()
          .toLowerCase();
        const plan = String(row.plan || "").trim();
        const estado = String(row.estado || "activo")
          .trim()
          .toLowerCase();
        const fechaNacimiento = String(row.fecha_nacimiento || "").trim();
        const telefono = String(row.telefono || "").trim();
        const errores: string[] = [];
        if (!nombre) errores.push("Falta nombre");
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
          errores.push("Email inválido");
        if (existing.has(email)) errores.push("Email ya registrado");
        if (seen.has(email)) errores.push("Email repetido en archivo");
        if (email) seen.add(email);
        if (plan && !planNames.has(plan.toLowerCase()))
          errores.push("Plan inexistente");
        if (!["activo", "inactivo", "vencido"].includes(estado))
          errores.push("Estado inválido");
        if (telefono && !/^\+?[0-9][0-9\s()-]{7,19}$/.test(telefono))
          errores.push("Teléfono inválido");
        if (fechaNacimiento) {
          const parsed = parseISODateLocal(fechaNacimiento);
          const validDate =
            /^\d{4}-\d{2}-\d{2}$/.test(fechaNacimiento) &&
            parsed &&
            formatISODateLocal(parsed) === fechaNacimiento &&
            parsed <= new Date();
          if (!validDate) errores.push("Fecha inválida (usa AAAA-MM-DD)");
        }
        return {
          fila: index + 2,
          nombre,
          email,
          telefono,
          fecha_nacimiento: fechaNacimiento,
          plan,
          estado,
          errores,
        };
      }),
    );
  };

  const importValidUsers = async () => {
    if (!canTransferUsers || !sucursalActiva?.id) return;
    const valid = importRows.filter((row) => row.errores.length === 0);
    if (!valid.length) return;
    setImportingUsers(true);
    let ok = 0;
    const failures: string[] = [];
    for (const row of valid) {
      const selectedPlan = planes.find(
        (p) => p.nombre.toLowerCase() === row.plan.toLowerCase(),
      );
      const start = formatISODateLocal(new Date());
      const { data, error: insertError } = await supabase
        .from("usuarios")
        .insert({
          nombre: row.nombre,
          email: row.email,
          telefono: row.telefono || null,
          fecha_nacimiento: row.fecha_nacimiento || null,
          tipo_cliente: "socio_gimnasio",
          tipo_membresia: selectedPlan?.nombre || null,
          fecha_inicio: selectedPlan ? start : null,
          fecha_vencimiento: selectedPlan
            ? calcularVencimientoPorPlan(selectedPlan.nombre, start, "") || null
            : null,
          estado: row.estado,
          gimnasio_id: sucursalActiva.gimnasio_id,
          sucursal_id: sucursalActiva.id,
          sesiones_disponibles: Number(selectedPlan?.max_sesiones_periodo || 0),
          requiere_captura_facial: false,
          notificar_email: true,
          notificar_whatsapp: true,
        })
        .select("id")
        .single();
      if (insertError || !data) {
        failures.push(
          `Fila ${row.fila}: ${insertError?.message || "No creada"}`,
        );
        continue;
      }
      try {
        const resetResult = await invokeResetPassword(data.id);
        if (!resetResult.ok)
          failures.push(
            `Fila ${row.fila}: creada sin clave temporal (${resetResult.body?.error || resetResult.body?.message || "servicio no disponible"})`,
          );
      } catch (err) {
        failures.push(
          `Fila ${row.fila}: creada sin clave temporal (${formatUnknownError(err, "error")})`,
        );
      }
      ok += 1;
    }
    setImportingUsers(false);
    setImportSummary(
      `${ok} cliente(s) importado(s).${failures.length ? ` ${failures.length} observación(es): ${failures.slice(0, 3).join(" · ")}` : ""}`,
    );
    setImportFailures(
      failures.map((detalle) => {
        const fila = Number(detalle.match(/^Fila (\d+)/)?.[1] || 0);
        return {
          fila,
          email: importRows.find((row) => row.fila === fila)?.email || "",
          detalle,
        };
      }),
    );
    setImportFinished(true);
    await fetchUsuarios();
  };

  const downloadImportErrors = () => {
    const validationErrors = importRows
      .filter((row) => row.errores.length > 0)
      .map((row) => ({
        fila: row.fila,
        email: row.email,
        detalle: row.errores.join(", "),
      }));
    const rows = [...validationErrors, ...importFailures];
    if (!rows.length) return;
    downloadUsersFile(
      rows,
      `errores-importacion-${formatISODateLocal(new Date())}.csv`,
    );
  };

  useEffect(() => {
    fetchUsuarios();
    fetchPlanes();
    fetchCoachesExternos();
  }, [sucursalActiva]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!faceCaptureOpen || !videoRef.current || !streamRef.current) return;

    const video = videoRef.current;
    video.srcObject = streamRef.current;
    const startPlayback = async () => {
      try {
        await video.play();
      } catch (err) {
        setFaceCaptureError(
          formatUnknownError(
            err,
            "No se pudo iniciar la vista de la cámara. Haz clic sobre el video para reintentar.",
          ),
        );
      }
    };
    startPlayback();
  }, [faceCaptureOpen]);

  const stopFaceCamera = () => {
    if (videoRef.current) videoRef.current.srcObject = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setFaceCaptureOpen(false);
  };

  const startFaceCamera = async () => {
    try {
      setFaceCaptureError(null);
      if (!navigator?.mediaDevices?.getUserMedia) {
        setFaceCaptureError("Este navegador no soporta camara.");
        return;
      }
      setFaceCaptureBusy(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 640 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setFaceCaptureOpen(true);
    } catch (err) {
      setFaceCaptureError(
        formatUnknownError(err, "No se pudo abrir la camara."),
      );
    } finally {
      setFaceCaptureBusy(false);
    }
  };

  const captureFaceSnapshot = () => {
    try {
      setFaceCaptureError(null);
      const video = videoRef.current;
      if (!video) {
        setFaceCaptureError("No se pudo acceder al video de la camara.");
        return;
      }
      if (
        video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ||
        !video.videoWidth ||
        !video.videoHeight
      ) {
        setFaceCaptureError(
          "La cámara todavía está iniciando. Espera un momento y vuelve a capturar.",
        );
        return;
      }
      const scale = Math.min(
        1,
        900 / Math.max(video.videoWidth, video.videoHeight),
      );
      const width = Math.round(video.videoWidth * scale);
      const height = Math.round(video.videoHeight * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        setFaceCaptureError("No se pudo capturar la imagen.");
        return;
      }
      ctx.drawImage(video, 0, 0, width, height);
      const snapshot = canvasToCameraJpeg(canvas);
      setFaceCaptureDataUrl(snapshot);
      stopFaceCamera();
    } catch (err) {
      setFaceCaptureError(
        formatUnknownError(err, "No se pudo capturar el rostro."),
      );
    }
  };

  const getValidAccessToken = async () => {
    let {
      data: { session },
    } = await supabase.auth.getSession();

    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session?.expires_at ?? 0;
    const isExpiredOrNearExpiry =
      !session?.access_token || expiresAt <= now + 60;

    if (isExpiredOrNearExpiry) {
      const { data: refreshed, error: refreshErr } =
        await supabase.auth.refreshSession();
      if (refreshErr || !refreshed?.session?.access_token) {
        throw new Error("Sesion expirada. Inicia sesion nuevamente.");
      }
      session = refreshed.session;
    }

    return session.access_token;
  };

  const invokeResetPassword = async (usuarioId: string) => {
    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as
      string | undefined;
    const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as
      string | undefined;

    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error("Configuración de Supabase incompleta en frontend.");
    }

    const invoke = async (accessToken: string) => {
      const response = await fetch(
        `${supabaseUrl}/functions/v1/usuarios-reset-password`,
        {
          method: "POST",
          headers: {
            apikey: supabaseAnonKey,
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            usuario_id: usuarioId,
          }),
        },
      );

      const body = await response.json().catch(() => ({}) as any);
      return { ok: response.ok, status: response.status, body };
    };

    const isInvalidJwt = (status: number, body: any) => {
      const txt = String(
        body?.details || body?.message || body?.error || "",
      ).toLowerCase();
      return (
        status === 401 ||
        txt.includes("invalid jwt") ||
        txt.includes("auth session missing") ||
        txt.includes("missing_bearer_token")
      );
    };

    let accessToken = await getValidAccessToken();
    let result = await invoke(accessToken);

    if (!result.ok && isInvalidJwt(result.status, result.body)) {
      const { data: refreshed, error: refreshErr } =
        await supabase.auth.refreshSession();
      if (refreshErr || !refreshed?.session?.access_token) {
        throw new Error("Tu sesión expiró. Vuelve a iniciar sesión.");
      }
      accessToken = refreshed.session.access_token;
      result = await invoke(accessToken);
    }

    return result;
  };

  const invokeSubscriptionManagement = async (
    payload: Record<string, unknown>,
  ) => {
    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as
      string | undefined;
    const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as
      string | undefined;
    if (!supabaseUrl || !supabaseAnonKey)
      throw new Error("Configuración de Supabase incompleta.");
    const execute = async (accessToken: string) => {
      const response = await fetch(
        `${supabaseUrl}/functions/v1/subscription-management`,
        {
          method: "POST",
          headers: {
            apikey: supabaseAnonKey,
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );
      const body = await response.json().catch(() => ({}) as any);
      return { ok: response.ok, status: response.status, body };
    };
    let token = await getValidAccessToken();
    let result = await execute(token);
    if (result.status === 401) {
      const { data: refreshed, error: refreshErr } =
        await supabase.auth.refreshSession();
      if (refreshErr || !refreshed.session?.access_token)
        throw new Error("Tu sesión expiró. Inicia sesión nuevamente.");
      token = refreshed.session.access_token;
      result = await execute(token);
    }
    return result;
  };

  const invokeMembershipTermination = async (payload: Record<string, unknown>) => {
    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string | undefined;
    const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string | undefined;
    if (!supabaseUrl || !supabaseAnonKey) throw new Error("Configuración de Supabase incompleta.");
    const execute = async (accessToken: string) => {
      const response = await fetch(`${supabaseUrl}/functions/v1/membership-termination`, {
        method: "POST",
        headers: { apikey: supabaseAnonKey, Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await response.json().catch(() => ({}) as any);
      return { ok: response.ok, status: response.status, body };
    };
    const token = await getValidAccessToken();
    let result = await execute(token);
    if (result.status === 401) {
      const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
      if (refreshErr || !refreshed.session?.access_token) throw new Error("Tu sesión expiró. Inicia sesión nuevamente.");
      result = await execute(refreshed.session.access_token);
    }
    return result;
  };

  const loadTerminationQuote = async (exceptionCode = terminationException) => {
    if (!subscriptionUser) return;
    setCancelBusy(true);
    setCancelResult(null);
    try {
      const result = await invokeMembershipTermination({ action: "quote", usuario_id: subscriptionUser.id, excepcion_codigo: exceptionCode });
      if (!result.ok) throw new Error(result.body?.error || "No se pudo calcular el cargo de término.");
      setTerminationQuote(result.body.quote as TerminationQuote);
    } catch (err) {
      setCancelResult(formatUnknownError(err, "No se pudo calcular el cargo de término."));
    } finally {
      setCancelBusy(false);
    }
  };

  const loadActiveSubscriptions = async () => {
    if (!canManageSubscriptions || !sucursalActiva?.id) {
      setRenovacionesByUsuario({});
      return;
    }
    try {
      const result = await invokeSubscriptionManagement({
        action: "list",
        sucursal_id: sucursalActiva.id,
      });
      if (!result.ok)
        throw new Error(
          result.body?.error || "No se pudieron consultar las renovaciones.",
        );
      const rows = (result.body?.suscripciones || []) as RenovacionAutomatica[];
      setRenovacionesByUsuario(
        Object.fromEntries(rows.map((row) => [row.usuario_id, row])),
      );
      const pending = await invokeMembershipTermination({ action: "list_pending", sucursal_id: sucursalActiva.id });
      if (pending.ok) {
        const items = (pending.body?.pendientes || []) as PendingTermination[];
        setPendingTerminations(Object.fromEntries(items.map((item) => [item.usuario_id, { ...item, aplica_cargo: Number(item.cargo_monto) > 0 }])));
      }
    } catch (err) {
      console.warn("No se pudieron cargar renovaciones automáticas:", err);
      setRenovacionesByUsuario({});
      setPendingTerminations({});
    }
  };

  const cancelAutomaticRenewal = async () => {
    if (!subscriptionUser || cancelReason.trim().length < 3) return;
    const confirmed = window.confirm(
      `¿Confirmas que deseas ${cancelType === "termination" ? "terminar anticipadamente la membresía" : "detener los próximos cobros"} de ${subscriptionUser.nombre}?\n\n` +
        "La renovación se cancelará y el acceso se mantendrá hasta el vencimiento ya pagado.",
    );
    if (!confirmed) return;
    setCancelBusy(true);
    setCancelResult(null);
    try {
      const hasAutomaticRenewal = Boolean(renovacionesByUsuario[subscriptionUser.id]);
      let result: { ok: boolean; body: any } = { ok: true, body: {} };
      if (hasAutomaticRenewal) {
        result = await invokeSubscriptionManagement({
          action: "cancel",
          usuario_id: subscriptionUser.id,
          motivo: cancelReason.trim(),
          notas: cancelNotes.trim(),
        });
        if (!result.ok)
          throw new Error(
            result.body?.error || "No se pudo cancelar la renovación automática.",
          );
      }
      if (cancelType === "termination") {
        const attached = await invokeMembershipTermination({
          action: "attach", usuario_id: subscriptionUser.id,
          excepcion_codigo: terminationException,
          excepcion_detalle: terminationExceptionDetail.trim(),
          motivo: cancelReason.trim(), notas: cancelNotes.trim(),
        });
        if (!attached.ok) throw new Error(attached.body?.error || "No se pudo generar la liquidación de término.");
        setTerminationQuote(attached.body.quote as TerminationQuote);
        setTerminationCancellationId(attached.body.termino?.id || null);
        setCancelResult(attached.body.quote?.cargo_monto > 0
          ? `${hasAutomaticRenewal ? "Renovación cancelada. " : ""}El cargo compensatorio quedó pendiente de pago.`
          : "Membresía terminada sin cargo compensatorio.");
      } else {
        setCancelResult(result.body?.message || "La renovación automática fue cancelada correctamente.");
      }
      await loadActiveSubscriptions();
    } catch (err) {
      setCancelResult(
        formatUnknownError(
          err,
          "No se pudo cancelar la renovación automática.",
        ),
      );
    } finally {
      setCancelBusy(false);
    }
  };

  const settleTerminationPayment = async () => {
    if (!subscriptionUser || !terminationCancellationId) return;
    setCancelBusy(true);
    try {
      const result = await invokeMembershipTermination({
        action: "settle", usuario_id: subscriptionUser.id,
        cancelacion_id: terminationCancellationId,
        metodo_pago: terminationPaymentMethod,
        numero_comprobante: terminationPaymentReference.trim(),
      });
      if (!result.ok) throw new Error(result.body?.error || "No se pudo registrar el pago del término.");
      setCancelResult(result.body?.message || "Cargo de término pagado.");
      setTerminationCancellationId(null);
      await loadActiveSubscriptions();
    } catch (err) {
      setCancelResult(formatUnknownError(err, "No se pudo registrar el pago del término."));
    } finally {
      setCancelBusy(false);
    }
  };

  const enrollFaceForUser = async (usuarioId: string) => {
    if (!faceCaptureDataUrl) {
      throw new Error("Debes capturar el rostro del usuario.");
    }

    const invokeEnroll = async (accessToken: string) =>
      supabase.functions.invoke("usuario-facial-enroll", {
        body: {
          usuario_id: usuarioId,
          image_data_url: faceCaptureDataUrl,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

    const resolveInvokeError = (invokeError: any) => {
      const status = invokeError?.context?.status;
      let details: any = null;

      if (invokeError?.context?.body) {
        try {
          details = JSON.parse(invokeError.context.body);
        } catch {
          details = null;
        }
      }

      const message = String(invokeError?.message || "").toLowerCase();
      const detailsMessage = String(
        details?.message || details?.error || "",
      ).toLowerCase();
      const invalidJwt =
        status === 401 ||
        message.includes("invalid jwt") ||
        detailsMessage.includes("invalid jwt");

      const fallbackMessage = details?.details
        ? `${details?.error || "Error"}: ${details.details}`
        : details?.error ||
          details?.message ||
          invokeError?.message ||
          "No se pudo guardar la biometria facial";

      return { invalidJwt, fallbackMessage };
    };

    let accessToken = await getValidAccessToken();
    let { error: invokeError } = await invokeEnroll(accessToken);

    if (invokeError) {
      const first = resolveInvokeError(invokeError);
      if (first.invalidJwt) {
        const { data: refreshed, error: refreshErr } =
          await supabase.auth.refreshSession();
        if (refreshErr || !refreshed?.session?.access_token) {
          throw new Error("Sesion expirada. Inicia sesion nuevamente.");
        }

        accessToken = refreshed.session.access_token;
        const retry = await invokeEnroll(accessToken);
        invokeError = retry.error;
      }
    }

    if (invokeError) {
      const parsed = resolveInvokeError(invokeError);
      throw new Error(parsed.fallbackMessage);
    }
  };

  const findPossibleDuplicate = async () => {
    if (editingUser || !sucursalActiva?.gimnasio_id) return null;
    const email = formData.email.trim().toLowerCase();
    const phone = String(formData.telefono || "").replace(/\D/g, "");
    const name = formData.nombre.trim().replace(/\s+/g, " ").toLowerCase();
    const { data, error: duplicateError } = await supabase
      .from("usuarios")
      .select("id,nombre,email,telefono,fecha_nacimiento,estado,sucursal_id,biometric_id")
      .eq("gimnasio_id", sucursalActiva.gimnasio_id);
    if (duplicateError) throw duplicateError;
    return (data || []).find((candidate: any) => {
      const candidateEmail = String(candidate.email || "").trim().toLowerCase();
      const candidatePhone = String(candidate.telefono || "").replace(/\D/g, "");
      const candidateName = String(candidate.nombre || "").trim().replace(/\s+/g, " ").toLowerCase();
      return (email && candidateEmail === email) ||
        (phone.length >= 8 && candidatePhone === phone) ||
        (name && candidateName === name && formData.fecha_nacimiento && candidate.fecha_nacimiento === formData.fecha_nacimiento);
    }) || null;
  };

  const linkExistingCameraFace = async (usuarioId: string, biometricId: number) => {
    if (!window.confirm(`La cámara ya conoce este rostro como ID ${biometricId}. ¿Deseas vincularlo a esta cuenta?`)) return;
    const { error: linkError } = await supabase.rpc("vincular_usuario_rostro_camara", {
      p_usuario_id: usuarioId,
      p_biometric_id: biometricId,
    });
    if (linkError) {
      setError(formatUnknownError(linkError, "No se pudo vincular el rostro existente."));
      return;
    }
    setError(null);
    await fetchUsuarios();
  };

  const fetchPlanes = async () => {
    try {
      const gimnasioId = sucursalActiva?.gimnasio_id;
      if (!gimnasioId) {
        setPlanes([]);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from("planes")
        .select("*")
        .eq("gimnasio_id", gimnasioId)
        .order("precio", { ascending: true });

      if (fetchError) throw fetchError;

      const rows = (data || []) as Plan[];
      // Be permissive: if "activo" is null/undefined, treat it as active.
      setPlanes(rows.filter((p) => p.activo !== false));
    } catch (err) {
      console.error("Error fetching planes:", err);
    }
  };

  const fetchCoachesExternos = async () => {
    const gimnasioId = sucursalActiva?.gimnasio_id;
    if (!gimnasioId) {
      setCoachesExternos([]);
      return;
    }

    const { data, error: coachesError } = await supabase
      .from("usuarios_admin")
      .select("id,nombre,email,sucursal_id")
      .eq("gimnasio_id", gimnasioId)
      .eq("role", "staff")
      .eq("tipo_staff", "coach_externo")
      .eq("activo", true)
      .order("nombre");

    if (coachesError) {
      console.error("Error cargando coaches externos:", coachesError);
      setCoachesExternos([]);
      return;
    }

    setCoachesExternos(
      ((data || []) as CoachExterno[]).filter(
        (coach) =>
          !coach.sucursal_id || coach.sucursal_id === sucursalActiva?.id,
      ),
    );
  };

  const fetchUsuarios = async () => {
    if (!sucursalActiva?.id) {
      setUsuarios([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const query = supabase
        .from("usuarios")
        .select("*")
        .eq("sucursal_id", sucursalActiva.id)
        .order("created_at", { ascending: false });

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      const usuariosRows = (data || []) as Usuario[];
      setUsuarios(usuariosRows);
      void loadActiveSubscriptions();

      const usuariosIds = usuariosRows.map((u) => u.id).filter(Boolean);
      if (usuariosIds.length === 0) {
        setBiometriaFacialByUsuario({});
        setSyncByUsuario({});
        setRiesgosByUsuario({});
        return;
      }

      if (canTransferUsers) {
        const desde = new Date(Date.now() - 28 * 86400000).toISOString();
        const { data: accesosRows, error: accesosError } = await supabase
          .from("control_acceso_eventos")
          .select("usuario_id, ocurrido_at")
          .eq("sucursal_id", sucursalActiva.id)
          .eq("tipo", "entrada")
          .eq("autorizado", true)
          .not("usuario_id", "is", null)
          .gte("ocurrido_at", desde)
          .order("ocurrido_at", { ascending: false });
        if (accesosError) {
          console.warn(
            "No se pudo calcular el riesgo de abandono:",
            accesosError.message,
          );
          setRiesgosByUsuario({});
        } else {
          const riesgos = calcularRiesgosFuga(usuariosRows, accesosRows || []);
          setRiesgosByUsuario(
            Object.fromEntries(riesgos.map((riesgo) => [riesgo.id, riesgo])),
          );
        }
      } else {
        setRiesgosByUsuario({});
      }

      const { data: facialRows, error: facialError } = await supabase
        .from("usuarios_biometria_facial")
        .select("usuario_id, estado, updated_at")
        .in("usuario_id", usuariosIds);

      if (facialError) {
        console.warn(
          "No se pudo cargar estado de biometría facial:",
          facialError.message,
        );
        setBiometriaFacialByUsuario({});
      } else {
        const facialMap: Record<string, BiometriaFacialRow> = {};
        for (const row of (facialRows || []) as BiometriaFacialRow[]) {
          if (!row?.usuario_id) continue;
          facialMap[row.usuario_id] = row;
        }
        setBiometriaFacialByUsuario(facialMap);
      }

      const { data: syncRows, error: syncError } = await supabase
        .from("control_acceso_sincronizaciones")
        .select(
          "usuario_id, estado, vinculado_tipo, vinculado_usuario_id, vinculado_admin_id, vinculado_biometric_id",
        )
        .in("usuario_id", usuariosIds);
      if (syncError) {
        console.warn(
          "No se pudo cargar sincronización facial:",
          syncError.message,
        );
        setSyncByUsuario({});
      } else {
        const summaries: Record<string, SyncSummary> = {};
        for (const row of syncRows || []) {
          const summary = summaries[row.usuario_id] || {
            total: 0,
            sincronizadas: 0,
            pendientes: 0,
            errores: 0,
            vinculados: [],
            idsPendientesVinculo: [],
          };
          summary.total += 1;
          if (row.estado === "sincronizado") summary.sincronizadas += 1;
          else if (row.estado === "error") summary.errores += 1;
          else summary.pendientes += 1;
          if (row.vinculado_biometric_id) {
            if (!row.vinculado_tipo && !row.vinculado_usuario_id && !row.vinculado_admin_id) {
              const orphanId = Number(row.vinculado_biometric_id);
              if (!summary.idsPendientesVinculo.includes(orphanId)) summary.idsPendientesVinculo.push(orphanId);
            } else {
              const label = row.vinculado_tipo === "administrativo"
                ? `cuenta administrativa · ID ${row.vinculado_biometric_id}`
                : `otra cuenta de socio · ID ${row.vinculado_biometric_id}`;
              if (!summary.vinculados.includes(label)) summary.vinculados.push(label);
            }
          }
          summaries[row.usuario_id] = summary;
        }
        setSyncByUsuario(summaries);
      }
    } catch (err) {
      setError(formatUnknownError(err, "Error al cargar usuarios"));
      console.error("Error fetching usuarios:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError(null);
      setFaceCaptureError(null);

      // Validar que exista una sucursal seleccionada
      if (!sucursalActiva?.id) {
        setError("Debe seleccionar una sucursal antes de crear un usuario");
        return;
      }

      if (
        !editingUser &&
        formData.requiere_captura_facial &&
        !faceCaptureDataUrl
      ) {
        setError(
          "Debes capturar el rostro para registrar el usuario (campo obligatorio).",
        );
        return;
      }

      if (
        formData.tipo_cliente === "cliente_externo" &&
        !formData.profesor_admin_id
      ) {
        setError("Debes asignar inmediatamente un coach externo.");
        return;
      }

      const possibleDuplicate = await findPossibleDuplicate();
      if (possibleDuplicate) {
        setError(`Ya existe una cuenta posiblemente duplicada para ${possibleDuplicate.nombre} (${possibleDuplicate.email}). Edita esa cuenta en lugar de crear otra.`);
        return;
      }

      const selectedPlan =
        formData.tipo_cliente === "socio_gimnasio"
          ? planes.find((p) => p.nombre === formData.tipo_membresia)
          : undefined;
      const shouldResetSessions =
        !editingUser ||
        (formData.tipo_membresia || "").trim().toLowerCase() !==
          (editingUser?.tipo_membresia || "").trim().toLowerCase();
      const sesionesIniciales =
        selectedPlan && Number(selectedPlan.max_sesiones_periodo || 0) > 0
          ? Number(selectedPlan.max_sesiones_periodo)
          : 0;
      const eraClienteExterno =
        editingUser?.tipo_cliente === "cliente_externo";
      const esClienteExterno =
        formData.tipo_cliente === "cliente_externo";
      const profesorAdminId = esClienteExterno
        ? formData.profesor_admin_id
        : editingUser && !eraClienteExterno
          // El panel general no administra el coach interno de un socio.
          // Se conserva la asignación existente hecha desde Progreso.
          ? editingUser.profesor_admin_id || null
          // Al convertir un cliente externo en socio sí se elimina el
          // patrocinio del coach externo.
          : null;

      const userData = {
        nombre: formData.nombre,
        email: formData.email,
        telefono: formData.telefono?.trim() ? formData.telefono.trim() : null,
        whatsapp: formData.whatsapp?.trim() ? formData.whatsapp.trim() : null,
        fecha_nacimiento: formData.fecha_nacimiento,
        tipo_cliente: formData.tipo_cliente,
        profesor_admin_id: profesorAdminId,
        tipo_membresia:
          formData.tipo_cliente === "socio_gimnasio"
            ? formData.tipo_membresia || null
            : null,
        fecha_inicio:
          formData.tipo_cliente === "socio_gimnasio"
            ? formData.fecha_inicio || null
            : null,
        fecha_vencimiento:
          formData.tipo_cliente !== "socio_gimnasio"
            ? null
            : formData.pago_exento
              ? formData.pago_exento_hasta || null
              : formData.fecha_vencimiento || null,
        permanencia_meses_contratada:
          formData.tipo_cliente === "socio_gimnasio" && selectedPlan
            ? formData.permanencia_meses_contratada
            : null,
        pago_exento:
          formData.tipo_cliente === "socio_gimnasio" && formData.pago_exento,
        pago_exento_motivo:
          formData.tipo_cliente === "socio_gimnasio" && formData.pago_exento
            ? formData.pago_exento_motivo.trim() || "Exención administrativa"
            : null,
        pago_exento_hasta:
          formData.tipo_cliente === "socio_gimnasio" && formData.pago_exento
            ? formData.pago_exento_hasta || null
            : null,
        estado: formData.estado,
        requiere_captura_facial: formData.requiere_captura_facial,
        notificar_email: formData.notificar_email,
        notificar_whatsapp: formData.notificar_whatsapp,
        gimnasio_id: sucursalActiva.gimnasio_id || null,
        sucursal_id: sucursalActiva.id,
        updated_at: new Date().toISOString(),
        ...(shouldResetSessions
          ? { sesiones_disponibles: sesionesIniciales }
          : {}),
      };

      if (editingUser) {
        const coachChanged =
          (editingUser.profesor_admin_id || "") !==
          (userData.profesor_admin_id || "");
        if (
          coachChanged &&
          (eraClienteExterno || esClienteExterno) &&
          !window.confirm(
            "¿Confirmas el cambio de coach? El entrenador anterior perderá acceso al chat y a la información de este cliente.",
          )
        )
          return;
        const { error: updateError } = await supabase
          .from("usuarios")
          .update(userData)
          .eq("id", editingUser.id);

        if (updateError) throw updateError;
        if (faceCaptureDataUrl) {
          await enrollFaceForUser(editingUser.id);
        }
      } else {
        // Crear nuevo usuario
        const { data: newUser, error: insertError } = await supabase
          .from("usuarios")
          .insert([userData])
          .select()
          .single();

        if (insertError) throw insertError;

        if (newUser && faceCaptureDataUrl) {
          try {
            await enrollFaceForUser(newUser.id);
          } catch (faceErr) {
            // If facial capture is mandatory, keep previous strict behavior.
            if (formData.requiere_captura_facial) {
              await supabase.from("usuarios").delete().eq("id", newUser.id);
              throw faceErr;
            }

            // Optional facial capture must not block user creation.
            console.warn(
              "No se pudo guardar biometria facial opcional:",
              faceErr,
            );
            setError(
              formatUnknownError(
                faceErr,
                "Usuario creado, pero no se pudo guardar la biometria facial",
              ),
            );
          }
        }

        // Preparar acceso con clave temporal automáticamente
        if (newUser) {
          try {
            const { ok, body: result } = await invokeResetPassword(newUser.id);
            if (!ok) {
              console.error("Error al preparar clave temporal:", result);
              setError(
                `Usuario creado, pero no se pudo generar clave temporal: ${result.error || "Error desconocido"}`,
              );
            } else if (result?.temporary_password) {
              alert(
                `Usuario creado con contraseña temporal.\n\nEmail: ${newUser.email}\nClave temporal: ${result.temporary_password}\n\nCompártela al usuario para que ingrese en la app móvil y luego cambie su clave.`,
              );
            }
          } catch (err) {
            console.error("Error al preparar clave temporal:", err);
            setError(
              formatUnknownError(
                err,
                "Usuario creado, pero no se pudo generar clave temporal",
              ),
            );
          }
        }
      }

      setShowModal(false);
      resetForm();
      fetchUsuarios();
    } catch (err) {
      setError(formatUnknownError(err, "Error al guardar usuario"));
      console.error("Error saving usuario:", err);
    }
  };

  const handleDelete = async (id: string) => {
    const usuario = usuarios.find((item) => item.id === id);
    const esInscripcionIncompleta =
      usuario?.registro_origen === "mobile_autonomo" &&
      ["email_pendiente", "pago_pendiente", "error"].includes(
        usuario?.onboarding_estado || "",
      );
    const pregunta = esInscripcionIncompleta
      ? "¿Liberar esta inscripción incompleta? La cuenta de acceso será eliminada y el correo podrá utilizarse nuevamente. El documento firmado se conservará para auditoría."
      : "¿Eliminar este usuario? Si ya tiene actividad, es preferible dejarlo inactivo para conservar su historial.";
    if (!confirm(pregunta)) return;

    try {
      setError(null);
      const { error: deleteError } = await supabase.rpc(
        "admin_eliminar_usuario_sin_pago",
        {
          p_usuario_id: id,
        },
      );
      if (deleteError) throw deleteError;

      fetchUsuarios();
    } catch (err) {
      setError(formatUnknownError(err, "Error al eliminar usuario"));
      console.error("Error deleting usuario:", err);
    }
  };

  const handleEdit = (usuario: Usuario) => {
    stopFaceCamera();
    setFaceCaptureDataUrl("");
    setFaceCaptureError(null);
    setEditingUser(usuario);
    setFormData({
      nombre: usuario.nombre,
      email: usuario.email,
      telefono: usuario.telefono,
      whatsapp: usuario.whatsapp,
      fecha_nacimiento: usuario.fecha_nacimiento,
      tipo_membresia: usuario.tipo_membresia,
      tipo_cliente: usuario.tipo_cliente || "socio_gimnasio",
      profesor_admin_id: usuario.profesor_admin_id || "",
      fecha_inicio: usuario.fecha_inicio,
      fecha_vencimiento: usuario.fecha_vencimiento,
      permanencia_meses_contratada:
        usuario.permanencia_meses_contratada === 6 ? 6 : 12,
      pago_exento: !!usuario.pago_exento,
      pago_exento_motivo: usuario.pago_exento_motivo || "",
      pago_exento_hasta: usuario.pago_exento_hasta || "",
      estado: usuario.estado,
      requiere_captura_facial: !!usuario.requiere_captura_facial,
      notificar_email: usuario.notificar_email,
      notificar_whatsapp: usuario.notificar_whatsapp,
    });
    setShowModal(true);
  };

  const handleResetPassword = async (usuario: Usuario) => {
    if (!confirm("Generar/restablecer clave temporal para este usuario?"))
      return;

    try {
      setError(null);
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as
        string | undefined;
      const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as
        string | undefined;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error("Configuracion de Supabase incompleta en frontend.");
      }

      const invokeReset = async (accessToken: string) => {
        const response = await fetch(
          `${supabaseUrl}/functions/v1/usuarios-reset-password`,
          {
            method: "POST",
            headers: {
              apikey: supabaseAnonKey,
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              usuario_id: usuario.id,
            }),
          },
        );

        const body = await response.json().catch(() => ({}) as any);
        return { ok: response.ok, status: response.status, body };
      };

      const resolveInvokeError = (status: number, details: any) => {
        const detailMessage = String(
          details?.details || details?.message || details?.error || "",
        ).toLowerCase();
        const invalidJwt =
          status === 401 ||
          detailMessage.includes("invalid jwt") ||
          detailMessage.includes("auth session missing") ||
          detailMessage.includes("missing_bearer_token");

        const fallbackMessage = details?.details
          ? `${details?.error || "Error"}: ${details.details}`
          : details?.error || details?.message || `Error HTTP ${status}`;

        return { invalidJwt, details, fallbackMessage };
      };

      let accessToken = await getValidAccessToken();
      let response = await invokeReset(accessToken);

      if (!response.ok) {
        const first = resolveInvokeError(response.status, response.body);
        if (first.invalidJwt) {
          const { data: refreshed, error: refreshErr } =
            await supabase.auth.refreshSession();
          if (refreshErr || !refreshed?.session?.access_token) {
            throw new Error("Tu sesion expiro. Vuelve a iniciar sesion.");
          }
          accessToken = refreshed.session.access_token;
          response = await invokeReset(accessToken);
        }
      }

      if (!response.ok) {
        const parsed = resolveInvokeError(response.status, response.body);
        throw new Error(parsed.fallbackMessage);
      }

      const data = response.body;
      if (data?.temporary_password) {
        alert(
          `Clave temporal generada para ${data.email || usuario.email}.\n\nClave temporal: ${data.temporary_password}\n\nEl usuario deberá cambiarla en la app móvil al iniciar sesión.`,
        );
        fetchUsuarios();
      } else if (data?.emailSent) {
        alert(`Link enviado a ${data.email}`);
      }
    } catch (err) {
      setError(
        formatUnknownError(err, "Error al enviar link de restablecimiento"),
      );
      console.error("Error reset password:", err);
    }
  };
  const handleAsignarPlan = async (plan: any) => {
    if (!selectedUser) return;

    try {
      setError(null);
      const fechaInicio = new Date();
      const fechaVencimiento = new Date();
      fechaVencimiento.setDate(fechaVencimiento.getDate() + plan.duracion_dias);

      const { error: updateError } = await supabase
        .from("usuarios")
        .update({
          tipo_membresia: plan.nombre,
          fecha_inicio: fechaInicio.toISOString().split("T")[0],
          fecha_vencimiento: fechaVencimiento.toISOString().split("T")[0],
          sesiones_disponibles:
            Number(plan.max_sesiones_periodo || 0) > 0
              ? Number(plan.max_sesiones_periodo)
              : 0,
          estado: "activo",
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedUser.id);

      if (updateError) throw updateError;

      setShowPlanModal(false);
      setSelectedUser(null);
      fetchUsuarios();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    stopFaceCamera();
    setFaceCaptureDataUrl("");
    setFaceCaptureError(null);
    setFormData({
      nombre: "",
      email: "",
      telefono: "",
      whatsapp: "",
      fecha_nacimiento: "",
      tipo_membresia: "",
      tipo_cliente: "socio_gimnasio",
      profesor_admin_id: "",
      fecha_inicio: "",
      fecha_vencimiento: "",
      permanencia_meses_contratada: 12,
      pago_exento: false,
      pago_exento_motivo: "",
      pago_exento_hasta: "",
      estado: "activo",
      requiere_captura_facial: false,
      notificar_email: true,
      notificar_whatsapp: true,
    });
    setEditingUser(null);
  };

  const filteredUsuarios = usuarios.filter((usuario) => {
    const matchesSearch =
      usuario.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      usuario.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEstado =
      filterEstado === "todos" || usuario.estado === filterEstado;
    const riesgo = riesgosByUsuario[usuario.id];
    const matchesRiesgo =
      filterRiesgo === "todos" ||
      (filterRiesgo === "riesgo" ? Boolean(riesgo) : false) ||
      (filterRiesgo === "normal" ? !riesgo : riesgo?.nivel === filterRiesgo);
    return matchesSearch && matchesEstado && matchesRiesgo;
  });

  const estadisticas = {
    total: usuarios.length,
    activos: usuarios.filter((u) => u.estado === "activo").length,
    vencidos: usuarios.filter((u) => u.estado === "vencido").length,
    inactivos: usuarios.filter((u) => u.estado === "inactivo").length,
    enRiesgo: Object.keys(riesgosByUsuario).length,
  };

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, { bg: string; text: string; icon: string }> =
      {
        teal: {
          bg: "bg-teal-100",
          text: "text-teal-600",
          icon: "text-teal-600",
        },
        emerald: {
          bg: "bg-emerald-100",
          text: "text-emerald-600",
          icon: "text-emerald-600",
        },
        amber: {
          bg: "bg-amber-100",
          text: "text-amber-600",
          icon: "text-amber-600",
        },
        rose: {
          bg: "bg-rose-100",
          text: "text-rose-600",
          icon: "text-rose-600",
        },
        orange: {
          bg: "bg-orange-100",
          text: "text-orange-600",
          icon: "text-orange-600",
        },
        cyan: {
          bg: "bg-cyan-100",
          text: "text-cyan-600",
          icon: "text-cyan-600",
        },
        lime: {
          bg: "bg-lime-100",
          text: "text-lime-600",
          icon: "text-lime-600",
        },
        fuchsia: {
          bg: "bg-fuchsia-100",
          text: "text-fuchsia-600",
          icon: "text-fuchsia-600",
        },
        sky: { bg: "bg-sky-100", text: "text-sky-600", icon: "text-sky-600" },
        red: { bg: "bg-red-100", text: "text-red-600", icon: "text-red-600" },
        yellow: {
          bg: "bg-yellow-100",
          text: "text-yellow-600",
          icon: "text-yellow-600",
        },
        slate: {
          bg: "bg-slate-100",
          text: "text-slate-600",
          icon: "text-slate-600",
        },
      };
    return colorMap[color] || colorMap.teal;
  };

  const getBiometriaBadge = (usuarioId: string) => {
    const row = biometriaFacialByUsuario[usuarioId];
    if (!row) {
      const sync = syncByUsuario[usuarioId];
      if (sync?.total > 0 && sync.sincronizadas === sync.total) {
        return { label: "Enrolado en cámaras", className: "bg-green-100 text-green-700" };
      }
      return { label: "Sin captura", className: "bg-gray-100 text-gray-700" };
    }
    if (row.estado === "enrolado") {
      return { label: "Enrolado", className: "bg-green-100 text-green-700" };
    }
    if (row.estado === "pendiente_validacion") {
      return { label: "Pendiente", className: "bg-amber-100 text-amber-700" };
    }
    return { label: "Rechazado", className: "bg-red-100 text-red-700" };
  };
  const editingBiometria = editingUser
    ? biometriaFacialByUsuario[editingUser.id]
    : null;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin"></i>
            <p className="mt-4 text-gray-600">Cargando usuarios...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-0 md:p-2 xl:p-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-5 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Gestión de Usuarios
            </h1>
            <p className="text-gray-600 mt-1">
              Administra los miembros del gimnasio
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {canTransferUsers && (
              <>
                <button
                  onClick={exportUsers}
                  className="justify-center border border-gray-300 px-4 py-3 rounded-lg flex items-center gap-2"
                >
                  <i className="ri-file-excel-2-line" />
                  Exportar
                </button>
                <button
                  onClick={() => {
                    setShowImportUsers(true);
                    setImportRows([]);
                    setImportSummary(null);
                    setImportFinished(false);
                    setImportFailures([]);
                  }}
                  className="justify-center border border-teal-600 text-teal-700 px-4 py-3 rounded-lg flex items-center gap-2"
                >
                  <i className="ri-upload-2-line" />
                  Importar
                </button>
              </>
            )}
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="w-full sm:w-auto justify-center bg-teal-500 text-white px-5 md:px-6 py-3 rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-user-add-line"></i>
              Nuevo Usuario
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}

        <div
          className={`grid grid-cols-2 ${canTransferUsers ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3 md:gap-6 mb-5 md:mb-8`}
        >
          <div className="bg-white p-3 md:p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Usuarios</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {estadisticas.total}
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 bg-blue-100 rounded-lg items-center justify-center">
                <i className="ri-group-line text-2xl text-blue-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 md:p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Activos</p>
                <p className="text-3xl font-bold text-green-600 mt-1">
                  {estadisticas.activos}
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 bg-green-100 rounded-lg items-center justify-center">
                <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 md:p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Vencidos</p>
                <p className="text-3xl font-bold text-red-600 mt-1">
                  {estadisticas.vencidos}
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 bg-red-100 rounded-lg items-center justify-center">
                <i className="ri-time-line text-2xl text-red-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 md:p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Inactivos</p>
                <p className="text-3xl font-bold text-gray-600 mt-1">
                  {estadisticas.inactivos}
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 bg-gray-100 rounded-lg items-center justify-center">
                <i className="ri-user-unfollow-line text-2xl text-gray-600"></i>
              </div>
            </div>
          </div>

          {canTransferUsers && (
            <button
              type="button"
              onClick={() =>
                setFilterRiesgo(filterRiesgo === "riesgo" ? "todos" : "riesgo")
              }
              aria-pressed={filterRiesgo === "riesgo"}
              className={`text-left bg-white p-3 md:p-6 rounded-xl shadow-sm border hover:border-red-300 transition-colors ${filterRiesgo === "riesgo" ? "border-red-400 ring-2 ring-red-100" : "border-red-100"}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">En riesgo</p>
                  <p className="text-3xl font-bold text-red-600 mt-1">
                    {estadisticas.enRiesgo}
                  </p>
                </div>
                <div className="hidden sm:flex w-12 h-12 bg-red-100 rounded-lg items-center justify-center">
                  <i className="ri-alarm-warning-line text-2xl text-red-600"></i>
                </div>
              </div>
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-6 mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            <select
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            >
              <option value="todos">Todos los estados</option>
              <option value="activo">Activos</option>
              <option value="vencido">Vencidos</option>
              <option value="inactivo">Inactivos</option>
            </select>
            {canTransferUsers && (
              <select
                value={filterRiesgo}
                onChange={(e) => setFilterRiesgo(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              >
                <option value="todos">Todos los riesgos</option>
                <option value="riesgo">Todos en riesgo</option>
                <option value="alto">Riesgo alto</option>
                <option value="medio">Requiere atención</option>
                <option value="normal">Sin señales</option>
              </select>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] table-fixed">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-[260px] px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="w-[170px] px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Membresía
                  </th>
                  {canTransferUsers && (
                    <th className="w-[120px] px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Riesgo
                    </th>
                  )}
                  <th className="w-[130px] px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Permanencia
                  </th>
                  <th className="w-[180px] px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Facial
                  </th>
                  <th className="sticky right-0 z-20 w-[300px] bg-gray-50 px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.45)]">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsuarios.map((usuario) => {
                  const biometria = getBiometriaBadge(usuario.id);
                  const biometriaRow = biometriaFacialByUsuario[usuario.id];
                  const sync = syncByUsuario[usuario.id];
                  const riesgo = riesgosByUsuario[usuario.id];
                  const contractProgress = getContractProgress(usuario);
                  return (
                    <tr
                      key={usuario.id}
                      className="group hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-4 align-top">
                        <div className="flex min-w-0 items-start gap-3">
                          <div className="w-10 h-10 shrink-0 bg-teal-100 rounded-full flex items-center justify-center">
                            <span className="text-teal-600 font-semibold text-sm">
                              {usuario.nombre.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0 max-w-[190px]">
                            <p className="font-medium text-gray-900 break-words line-clamp-2">
                              {usuario.nombre}
                            </p>
                            <p className="text-sm text-gray-500 break-all line-clamp-3">
                              {usuario.email}
                            </p>
                            {usuario.tipo_cliente === "cliente_externo" && (
                              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                <span className="inline-flex px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-semibold">
                                  Cliente externo
                                </span>
                                <span className="text-xs text-gray-500">
                                  Coach:{" "}
                                  {coachesExternos.find(
                                    (coach) =>
                                      coach.id === usuario.profesor_admin_id,
                                  )?.nombre || "No disponible"}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${usuario.tipo_cliente === "cliente_externo" ? "bg-amber-100 text-amber-800" : "bg-purple-100 text-purple-700"}`}
                        >
                          {usuario.tipo_cliente === "cliente_externo"
                            ? "Plan del coach"
                            : usuario.pago_exento
                              ? "Libre de pago"
                              : usuario.tipo_membresia || "Sin plan"}
                        </span>
                        {usuario.pago_exento && usuario.tipo_membresia && (
                          <p className="mt-1 text-xs text-gray-500">
                            Plan de uso: {usuario.tipo_membresia}
                          </p>
                        )}
                      </td>
                      {canTransferUsers && (
                        <td className="px-4 py-4 align-top">
                          {riesgo ? (
                            <button
                              type="button"
                              onClick={() => setSelectedRiesgo(riesgo)}
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${riesgo.nivel === "alto" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}
                              title="Ver motivos del riesgo"
                            >
                              <i
                                className={
                                  riesgo.nivel === "alto"
                                    ? "ri-alarm-warning-fill"
                                    : "ri-error-warning-fill"
                                }
                              ></i>
                              {riesgo.nivel === "alto" ? "Alto" : "Atención"} ·{" "}
                              {riesgo.puntaje}
                            </button>
                          ) : (
                            <span className="inline-flex px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                              Normal
                            </span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-4 align-top">
                        {contractProgress ? (
                          <div>
                            <p className="text-base font-bold text-gray-900">
                              {contractProgress.current}/{contractProgress.total}
                            </p>
                            <p className="text-xs text-gray-500">
                              {contractProgress.completed
                                ? "Permanencia cumplida"
                                : "Mes en curso"}
                            </p>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">Sin plazo registrado</span>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-col gap-1">
                          <span
                            className={`inline-flex items-center w-fit px-3 py-1 rounded-full text-xs font-semibold ${biometria.className}`}
                          >
                            {biometria.label}
                          </span>
                          {sync && (
                            <div className="flex flex-col gap-0.5">
                              <span
                                className={`text-xs ${sync.errores ? "text-red-600" : sync.pendientes ? "text-amber-600" : "text-emerald-600"}`}
                              >
                                Cámaras: {sync.sincronizadas}/{sync.total}
                                {sync.errores
                                  ? ` · ${sync.errores} error`
                                  : sync.pendientes
                                    ? ` · ${sync.pendientes} pendiente`
                                    : " · al día"}
                              </span>
                              {sync.vinculados.map((label) => (
                                <span
                                  key={label}
                                  className="text-[11px] text-blue-700"
                                >
                                  Rostro registrado con {label}
                                </span>
                              ))}
                              {sync.idsPendientesVinculo.map((biometricId) => (
                                <button
                                  key={biometricId}
                                  type="button"
                                  onClick={() => void linkExistingCameraFace(usuario.id, biometricId)}
                                  className="mt-1 w-fit rounded-lg border border-blue-300 bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-100"
                                >
                                  Vincular rostro existente · ID {biometricId}
                                </button>
                              ))}
                            </div>
                          )}
                          {biometriaRow?.updated_at && (
                            <span className="text-xs text-gray-500">
                              {new Date(biometriaRow.updated_at).toLocaleString(
                                "es-CL",
                              )}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="sticky right-0 z-10 bg-white px-4 py-4 align-top shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.45)] group-hover:bg-gray-50">
                        <div className="flex flex-wrap items-center gap-1">
                          <button
                            onClick={() => handleEdit(usuario)}
                            className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            onClick={() => {
                              handleEdit(usuario);
                              setTimeout(() => {
                                startFaceCamera().catch(() => {});
                              }, 120);
                            }}
                            className="w-8 h-8 flex items-center justify-center text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Recapturar rostro"
                          >
                            <i className="ri-camera-line"></i>
                          </button>
                          <button
                            onClick={() => handleResetPassword(usuario)}
                            className="w-8 h-8 flex items-center justify-center text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="Generar/restablecer clave temporal"
                          >
                            <i className="ri-key-line"></i>
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(usuario);
                              setShowPlanModal(true);
                            }}
                            className="w-8 h-8 flex items-center justify-center text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                            title="Asignar Plan"
                          >
                            <i className="ri-vip-crown-line"></i>
                          </button>
                          {canManageSubscriptions && (
                            <button
                                onClick={() => {
                                  const pending = pendingTerminations[usuario.id];
                                  setSubscriptionUser(usuario);
                                  setCancelReason("Solicitud del cliente");
                                  setCancelNotes("");
                                  setCancelResult(pending ? "El cargo compensatorio de este término se encuentra pendiente de pago." : null);
                                  setCancelType(pending ? "termination" : "renewal");
                                  setTerminationQuote(pending || null);
                                  setTerminationException("ninguna");
                                  setTerminationExceptionDetail("");
                                  setTerminationCancellationId(pending?.id || null);
                                  setTerminationPaymentMethod("efectivo");
                                  setTerminationPaymentReference("");
                                }}
                                className={`h-8 px-2.5 inline-flex items-center justify-center gap-1.5 rounded-lg border transition-colors ${renovacionesByUsuario[usuario.id] || pendingTerminations[usuario.id] ? "text-orange-700 bg-orange-50 hover:bg-orange-100 border-orange-200" : "text-blue-700 bg-blue-50 hover:bg-blue-100 border-blue-200"}`}
                                title={pendingTerminations[usuario.id] ? "Registrar cargo de término pendiente" : renovacionesByUsuario[usuario.id] ? "Gestionar renovación automática" : "Configurar renovación automática"}
                              >
                                <i className="ri-repeat-2-line"></i>
                                <span className="text-xs font-semibold">Renovación</span>
                              </button>
                          )}
                          <button
                            onClick={() => handleDelete(usuario.id)}
                            className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredUsuarios.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-user-line text-5xl text-gray-300"></i>
              <p className="text-gray-500 mt-4">No se encontraron usuarios</p>
            </div>
          )}
        </div>

        {selectedRiesgo && (
          <div
            className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
            onClick={() => setSelectedRiesgo(null)}
          >
            <div
              className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="p-5 border-b border-gray-200 flex items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedRiesgo.nombre}
                    </h2>
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold ${selectedRiesgo.nivel === "alto" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}
                    >
                      Riesgo {selectedRiesgo.nivel} · {selectedRiesgo.puntaje}
                      /100
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Señales detectadas automáticamente
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedRiesgo(null)}
                  className="w-9 h-9 rounded-lg hover:bg-gray-100"
                  aria-label="Cerrar"
                >
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>
              <div className="p-5 space-y-4 overflow-y-auto">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-gray-50 p-3">
                    <p className="text-xs text-gray-500">Última asistencia</p>
                    <p className="font-semibold text-gray-900 mt-1">
                      {selectedRiesgo.ultimaAsistencia
                        ? new Date(
                            selectedRiesgo.ultimaAsistencia,
                          ).toLocaleDateString("es-CL")
                        : "Sin registro"}
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3">
                    <p className="text-xs text-gray-500">Días sin asistir</p>
                    <p className="font-semibold text-gray-900 mt-1">
                      {selectedRiesgo.diasSinAsistir}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-2">
                    Motivos
                  </p>
                  <ul className="space-y-2">
                    {selectedRiesgo.motivos.map((motivo) => (
                      <li
                        key={motivo}
                        className="flex items-start gap-2 text-sm text-gray-700"
                      >
                        <i className="ri-error-warning-line text-amber-600 mt-0.5"></i>
                        <span>{motivo}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-xs text-gray-500">
                  Este indicador ayuda a priorizar seguimiento; no bloquea al
                  socio ni modifica su membresía.
                </p>
              </div>
            </div>
          </div>
        )}

        {subscriptionUser && (
          <div
            className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
            onClick={() => !cancelBusy && setSubscriptionUser(null)}
          >
            <div
              className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="p-5 border-b flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Gestión de membresía
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {subscriptionUser.nombre} · {subscriptionUser.email}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={cancelBusy}
                  onClick={() => setSubscriptionUser(null)}
                  className="w-9 h-9 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  aria-label="Cerrar"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>
              <div className="p-5 space-y-4 overflow-y-auto">
                {(() => {
                  const progress = getContractProgress(subscriptionUser);
                  return (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800">
                      <p><strong>Plan:</strong> {subscriptionUser.tipo_membresia || "Sin plan"}</p>
                      <p className="mt-1">
                        <strong>Permanencia:</strong>{" "}
                        {progress
                          ? `${progress.current}/${progress.total} meses${progress.completed ? " · cumplida" : ""}`
                          : "Sin plazo contractual registrado"}
                      </p>
                    </div>
                  );
                })()}
                {(() => {
                  const renewal = renovacionesByUsuario[subscriptionUser.id];
                  const pending = pendingTerminations[subscriptionUser.id];
                  return renewal ? (
                    <div className="rounded-lg bg-blue-50 border border-blue-100 p-4 text-sm text-blue-900">
                      <p>
                        <strong>Proveedor:</strong>{" "}
                        {renewal?.proveedor === "fintoc"
                          ? "Fintoc"
                          : renewal?.proveedor === "flow"
                            ? "Flow"
                          : renewal?.proveedor === "mercadopago"
                            ? "Mercado Pago"
                            : "Transbank Oneclick"}
                      </p>
                      <p className="mt-1">
                        <strong>Próximo cobro:</strong>{" "}
                        {renewal?.proximo_cobro_at
                          ? new Date(
                              renewal.proximo_cobro_at,
                            ).toLocaleDateString("es-CL")
                          : "No informado"}
                      </p>
                      <p className="mt-1">
                        <strong>Acceso pagado hasta:</strong>{" "}
                        {subscriptionUser.fecha_vencimiento
                          ? new Date(
                              `${subscriptionUser.fecha_vencimiento}T12:00:00`,
                            ).toLocaleDateString("es-CL")
                          : "No informado"}
                      </p>
                    </div>
                  ) : pending ? (
                    <div className="rounded-lg bg-orange-50 border border-orange-200 p-4 text-sm text-orange-900">
                      La renovación ya fue cancelada. Queda pendiente registrar el cargo compensatorio del término anticipado.
                    </div>
                  ) : (
                    <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-900">
                      <p className="font-semibold">Renovación automática no asociada</p>
                      <p className="mt-1">
                        Esta membresía puede tener pagos manuales, por máquina o por Transbank. El plazo contractual continúa normalmente aunque todavía no exista un cobro automático asociado.
                      </p>
                      <p className="mt-2 text-xs text-blue-700">
                        Desde este botón se iniciará la adhesión cuando el proveedor de renovación automática esté disponible para el portal, la tablet y la aplicación móvil.
                      </p>
                    </div>
                  );
                })()}
                {!cancelResult && !pendingTerminations[subscriptionUser.id] && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {renovacionesByUsuario[subscriptionUser.id] && (
                        <button
                          type="button"
                          onClick={() => {
                            setCancelType("renewal");
                            setTerminationQuote(null);
                          }}
                          className={`rounded-lg border p-3 text-left ${cancelType === "renewal" ? "border-blue-500 bg-blue-50" : "border-gray-200"}`}
                        >
                          <strong className="block text-sm">Cancelar renovación</strong>
                          <span className="text-xs text-gray-600">Detiene solamente los próximos cobros.</span>
                        </button>
                      )}
                      {!renovacionesByUsuario[subscriptionUser.id] && (
                        <button
                          type="button"
                          onClick={() => window.open(`${window.location.origin}/renovacion`, "_blank", "noopener,noreferrer")}
                          className="rounded-lg border border-emerald-500 bg-emerald-50 p-3 text-left hover:bg-emerald-100"
                        >
                          <strong className="block text-sm text-emerald-800">Inscribir recurrente</strong>
                          <span className="text-xs text-emerald-700">Abre el flujo seguro para registrar el medio de pago.</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setCancelType("termination");
                          void loadTerminationQuote("ninguna");
                        }}
                        className={`rounded-lg border p-3 text-left ${cancelType === "termination" ? "border-red-500 bg-red-50" : "border-gray-200"}`}
                      >
                        <strong className="block text-sm">Término anticipado</strong>
                        <span className="text-xs text-gray-600">Calcula el cargo contractual y finaliza la membresía.</span>
                      </button>
                    </div>
                    {cancelType === "termination" && (
                      <div className="space-y-3 rounded-lg border border-orange-200 bg-orange-50 p-4">
                        <label className="block">
                          <span className="text-sm font-semibold text-gray-700">Excepción contractual</span>
                          <select
                            value={terminationException}
                            onChange={(event) => {
                              const value = event.target.value;
                              setTerminationException(value);
                              setTerminationQuote(null);
                              void loadTerminationQuote(value);
                            }}
                            className="mt-1 w-full border rounded-lg px-3 py-2 bg-white"
                          >
                            <option value="ninguna">No aplica excepción</option>
                            <option value="salud">Salud o imposibilidad acreditada</option>
                            <option value="fallecimiento">Fallecimiento</option>
                            <option value="incumplimiento_mowe">Incumplimiento imputable a Mowe</option>
                            <option value="cierre_sede">Cierre definitivo de la sede</option>
                            <option value="derecho_legal">Derecho legal de término sin cargo</option>
                          </select>
                        </label>
                        {terminationException !== "ninguna" && (
                          <label className="block">
                            <span className="text-sm font-semibold text-gray-700">Respaldo de la excepción</span>
                            <textarea value={terminationExceptionDetail} onChange={(event) => setTerminationExceptionDetail(event.target.value)} rows={2} className="mt-1 w-full border rounded-lg px-3 py-2 bg-white" placeholder="Describe el documento o antecedente revisado" />
                          </label>
                        )}
                        {terminationQuote ? (
                          <div className="rounded-lg bg-white border p-3 text-sm space-y-1">
                            <p><strong>Permanencia:</strong> {terminationQuote.permanencia_meses} meses</p>
                            <p><strong>Mensualidades cumplidas:</strong> {terminationQuote.mensualidades_cumplidas}</p>
                            <p><strong>Mensualidades restantes:</strong> {terminationQuote.mensualidades_restantes}</p>
                            <p><strong>Valor mensual firmado:</strong> ${Number(terminationQuote.valor_mensual).toLocaleString("es-CL")}</p>
                            <p className="pt-1 text-base"><strong>Cargo compensatorio (20%):</strong> ${Number(terminationQuote.cargo_monto).toLocaleString("es-CL")}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-orange-800">Calculando liquidación contractual…</p>
                        )}
                      </div>
                    )}
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
                      {renovacionesByUsuario[subscriptionUser.id]
                        ? "Se cancelarán los cobros futuros. Los pagos anteriores no se reembolsan y el acceso ya pagado se conserva."
                        : "Esta membresía no tiene cobro automático. El término quedará igualmente registrado y se calculará con la permanencia firmada."}
                    </div>
                    <label className="block">
                      <span className="text-sm font-semibold text-gray-700">
                        Motivo
                      </span>
                      <select
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="mt-1 w-full border rounded-lg px-3 py-2"
                      >
                        <option>Solicitud del cliente</option>
                        <option>Cambio de plan</option>
                        <option>Problema con el medio de pago</option>
                        <option>Otro</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-sm font-semibold text-gray-700">
                        Notas (opcional)
                      </span>
                      <textarea
                        value={cancelNotes}
                        onChange={(e) => setCancelNotes(e.target.value)}
                        rows={3}
                        className="mt-1 w-full border rounded-lg px-3 py-2"
                        placeholder="Detalle de la solicitud o canal de atención"
                      />
                    </label>
                  </>
                )}
                {cancelResult && (
                  <div
                    className={`rounded-lg border p-4 text-sm ${renovacionesByUsuario[subscriptionUser.id] ? "bg-red-50 border-red-200 text-red-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}
                  >
                    {cancelResult}
                  </div>
                )}
                {terminationCancellationId && terminationQuote && terminationQuote.cargo_monto > 0 && (
                  <div className="rounded-lg border border-teal-200 bg-teal-50 p-4 space-y-3">
                    <div>
                      <p className="font-semibold text-teal-900">Registrar pago del término</p>
                      <p className="text-sm text-teal-800">Monto: ${Number(terminationQuote.cargo_monto).toLocaleString("es-CL")}. Este pago no extenderá la membresía.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <select value={terminationPaymentMethod} onChange={(event) => setTerminationPaymentMethod(event.target.value)} className="border rounded-lg px-3 py-2 bg-white">
                        <option value="efectivo">Efectivo</option>
                        <option value="tarjeta">Tarjeta</option>
                        <option value="transferencia">Transferencia</option>
                        <option value="otro">Otro</option>
                      </select>
                      <input value={terminationPaymentReference} onChange={(event) => setTerminationPaymentReference(event.target.value)} className="border rounded-lg px-3 py-2" placeholder="Comprobante (opcional)" />
                    </div>
                    <button type="button" disabled={cancelBusy} onClick={() => void settleTerminationPayment()} className="w-full px-4 py-2 bg-teal-700 text-white rounded-lg disabled:opacity-50">
                      {cancelBusy ? "Registrando…" : "Confirmar pago del término"}
                    </button>
                  </div>
                )}
              </div>
              <div className="p-4 border-t flex justify-end gap-3">
                <button
                  type="button"
                  disabled={cancelBusy}
                  onClick={() => setSubscriptionUser(null)}
                  className="px-4 py-2 border rounded-lg disabled:opacity-50"
                >
                  {cancelResult && !renovacionesByUsuario[subscriptionUser.id]
                    ? "Cerrar"
                    : "Volver"}
                </button>
                {!cancelResult && !pendingTerminations[subscriptionUser.id] && (Boolean(renovacionesByUsuario[subscriptionUser.id]) || cancelType === "termination") && (
                  <button
                    type="button"
                    disabled={cancelBusy || (cancelType === "termination" && (!terminationQuote || (terminationException !== "ninguna" && terminationExceptionDetail.trim().length < 3)))}
                    onClick={() => void cancelAutomaticRenewal()}
                    className="px-5 py-2 bg-red-600 text-white rounded-lg disabled:opacity-50"
                  >
                    {cancelBusy
                      ? "Cancelando…"
                      : cancelType === "termination"
                        ? "Confirmar término anticipado"
                        : "Cancelar renovación automática"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {showImportUsers && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
              <div className="p-5 border-b flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Importar clientes</h2>
                  <p className="text-sm text-gray-500">
                    Revisa los datos antes de confirmar.
                  </p>
                </div>
                <button
                  onClick={() => setShowImportUsers(false)}
                  className="p-2"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>
              <div className="p-5 overflow-y-auto space-y-4">
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={downloadImportTemplate}
                    className="px-4 py-2 border rounded-lg text-sm"
                  >
                    <i className="ri-download-line mr-2" />
                    Descargar plantilla
                  </button>
                  <label className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm cursor-pointer">
                    <i className="ri-file-upload-line mr-2" />
                    Seleccionar CSV
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void parseImportFile(file);
                      }}
                    />
                  </label>
                </div>
                {importRows.length > 0 && (
                  <>
                    <div className="text-sm">
                      <strong>{importRows.length}</strong> filas ·{" "}
                      <span className="text-green-700">
                        {importRows.filter((r) => !r.errores.length).length}{" "}
                        válidas
                      </span>{" "}
                      ·{" "}
                      <span className="text-red-700">
                        {importRows.filter((r) => r.errores.length).length} con
                        errores
                      </span>
                    </div>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-3 text-left">Fila</th>
                            <th className="p-3 text-left">Nombre</th>
                            <th className="p-3 text-left">Email</th>
                            <th className="p-3 text-left">Plan</th>
                            <th className="p-3 text-left">Validación</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {importRows.map((row) => (
                            <tr key={row.fila}>
                              <td className="p-3">{row.fila}</td>
                              <td className="p-3">{row.nombre || "—"}</td>
                              <td className="p-3">{row.email || "—"}</td>
                              <td className="p-3">{row.plan || "Sin plan"}</td>
                              <td
                                className={`p-3 ${row.errores.length ? "text-red-700" : "text-green-700"}`}
                              >
                                {row.errores.join(", ") ||
                                  "Lista para importar"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                {importSummary && (
                  <div className="p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg text-sm">
                    {importSummary}
                  </div>
                )}
                {(importRows.some((r) => r.errores.length > 0) ||
                  importFailures.length > 0) && (
                  <button
                    onClick={downloadImportErrors}
                    className="px-4 py-2 border border-red-200 text-red-700 rounded-lg text-sm"
                  >
                    <i className="ri-file-warning-line mr-2" />
                    Descargar informe de errores
                  </button>
                )}
              </div>
              <div className="p-4 border-t flex justify-end gap-3">
                <button
                  onClick={() => setShowImportUsers(false)}
                  className="px-4 py-2 border rounded-lg"
                >
                  Cerrar
                </button>
                <button
                  onClick={() => void importValidUsers()}
                  disabled={
                    importingUsers ||
                    importFinished ||
                    !importRows.some((r) => !r.errores.length)
                  }
                  className="px-5 py-2 bg-teal-600 text-white rounded-lg disabled:opacity-50"
                >
                  {importingUsers
                    ? "Importando…"
                    : importFinished
                      ? "Importación procesada"
                      : "Importar filas válidas"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingUser ? "Editar Usuario" : "Nuevo Usuario"}
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nombre}
                      onChange={(e) =>
                        setFormData({ ...formData, nombre: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono (opcional)
                    </label>
                    <input
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) =>
                        setFormData({ ...formData, telefono: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      WhatsApp (opcional)
                    </label>
                    <input
                      type="tel"
                      value={formData.whatsapp}
                      onChange={(e) =>
                        setFormData({ ...formData, whatsapp: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Nacimiento *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.fecha_nacimiento}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          fecha_nacimiento: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estado *
                    </label>
                    <select
                      required
                      value={formData.estado}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          estado: e.target.value as
                            "activo" | "inactivo" | "vencido",
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    >
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                      <option value="vencido">Vencido</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de cliente *
                    </label>
                    <select
                      required
                      value={formData.tipo_cliente}
                      onChange={(e) => {
                        const tipo = e.target.value as
                          "socio_gimnasio" | "cliente_externo";
                        setFormData({
                          ...formData,
                          tipo_cliente: tipo,
                          profesor_admin_id:
                            tipo === "cliente_externo"
                              ? formData.profesor_admin_id
                              : "",
                          tipo_membresia:
                            tipo === "cliente_externo"
                              ? ""
                              : formData.tipo_membresia,
                          fecha_inicio:
                            tipo === "cliente_externo"
                              ? ""
                              : formData.fecha_inicio,
                          fecha_vencimiento:
                            tipo === "cliente_externo"
                              ? ""
                              : formData.fecha_vencimiento,
                        });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    >
                      <option value="socio_gimnasio">Socio del gimnasio</option>
                      <option value="cliente_externo">
                        Cliente de coach externo
                      </option>
                    </select>
                  </div>

                  {formData.tipo_cliente === "cliente_externo" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Coach externo asignado *
                      </label>
                      <select
                        required
                        value={formData.profesor_admin_id}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            profesor_admin_id: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      >
                        <option value="">Seleccionar coach</option>
                        {coachesExternos.map((coach) => (
                          <option key={coach.id} value={coach.id}>
                            {coach.nombre} · {coach.email}
                          </option>
                        ))}
                      </select>
                      {coachesExternos.length === 0 && (
                        <p className="text-xs text-amber-700 mt-2">
                          Primero registra un Staff con tipo “Coach externo” en
                          Configuración → Usuarios Admin.
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Plan de Membresía{" "}
                      {formData.tipo_cliente === "cliente_externo"
                        ? "(no corresponde)"
                        : "(opcional)"}
                    </label>
                    {planes.length > 0 ? (
                      <select
                        disabled={formData.tipo_cliente === "cliente_externo"}
                        value={formData.tipo_membresia}
                        onChange={(e) => {
                          const next = e.target.value;
                          const nextPlan = planes.find((plan) => plan.nombre === next);
                          const nextInicio =
                            formData.fecha_inicio ||
                            formatISODateLocal(new Date());
                          const nextVenc = next
                            ? calcularVencimientoPorPlan(
                                next,
                                nextInicio,
                                formData.fecha_vencimiento,
                              )
                            : formData.fecha_vencimiento;

                          setFormData({
                            ...formData,
                            tipo_membresia: next,
                            // Auto-fill dates when a plan is selected (user can still edit them afterwards).
                            fecha_inicio: next
                              ? nextInicio
                              : formData.fecha_inicio,
                            fecha_vencimiento: next
                              ? nextVenc
                              : formData.fecha_vencimiento,
                            permanencia_meses_contratada:
                              nextPlan?.permanencia_meses === 6 ? 6 : 12,
                          });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <option value="">Sin plan</option>
                        {planes.map((p) => (
                          <option key={p.id} value={p.nombre}>
                            {p.nombre} ({p.duracion_dias} días)
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-sm text-gray-500 border border-gray-200 rounded-lg px-4 py-2">
                        No hay planes cargados (o no se pudieron cargar). Puedes
                        crear planes en "Planes de Membresía".
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Permanencia mínima *
                    </label>
                    <select
                      required={
                        formData.tipo_cliente === "socio_gimnasio" &&
                        Boolean(formData.tipo_membresia)
                      }
                      disabled={
                        formData.tipo_cliente !== "socio_gimnasio" ||
                        !formData.tipo_membresia
                      }
                      value={formData.permanencia_meses_contratada}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          permanencia_meses_contratada:
                            Number(e.target.value) === 6 ? 6 : 12,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <option value={6}>6 meses</option>
                      <option value={12}>12 meses</option>
                    </select>
                    {!formData.tipo_membresia && (
                      <p className="mt-1 text-xs text-gray-500">
                        Selecciona un plan para definir la permanencia.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Inicio
                    </label>
                    <input
                      type="date"
                      disabled={formData.tipo_cliente === "cliente_externo"}
                      value={formData.fecha_inicio}
                      onChange={(e) => {
                        const nextInicio = e.target.value;
                        const nextVenc = formData.tipo_membresia
                          ? calcularVencimientoPorPlan(
                              formData.tipo_membresia,
                              nextInicio,
                              formData.fecha_vencimiento,
                            )
                          : formData.fecha_vencimiento;

                        setFormData({
                          ...formData,
                          fecha_inicio: nextInicio,
                          fecha_vencimiento: nextVenc,
                        });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm disabled:bg-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Vencimiento
                    </label>
                    <input
                      type="date"
                      disabled={
                        formData.tipo_cliente === "cliente_externo" ||
                        formData.pago_exento
                      }
                      value={formData.fecha_vencimiento}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          fecha_vencimiento: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm disabled:bg-gray-100"
                    />
                  </div>

                  {formData.tipo_cliente === "socio_gimnasio" && (
                    <div className="md:col-span-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.pago_exento}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              pago_exento: e.target.checked,
                              pago_exento_motivo: e.target.checked
                                ? formData.pago_exento_motivo ||
                                  "Personal del gimnasio"
                                : "",
                              pago_exento_hasta: e.target.checked
                                ? formData.pago_exento_hasta
                                : "",
                            })
                          }
                          className="mt-0.5 w-5 h-5 text-emerald-600 rounded"
                        />
                        <span>
                          <span className="block text-sm font-semibold text-emerald-900">
                            Libre de pago
                          </span>
                          <span className="block text-xs text-emerald-700 mt-1">
                            Mantiene acceso y plan de entrenamiento sin crear
                            pagos ni sumar ingresos.
                          </span>
                        </span>
                      </label>
                      {formData.pago_exento && (
                        <div className="grid md:grid-cols-2 gap-3 mt-4">
                          <label className="text-sm text-gray-700">
                            Motivo
                            <input
                              value={formData.pago_exento_motivo}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  pago_exento_motivo: e.target.value,
                                })
                              }
                              placeholder="Ej: Personal del gimnasio"
                              className="mt-1 w-full px-3 py-2 border border-emerald-200 rounded-lg bg-white"
                            />
                          </label>
                          <label className="text-sm text-gray-700">
                            Vigente hasta (opcional)
                            <input
                              type="date"
                              value={formData.pago_exento_hasta}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  pago_exento_hasta: e.target.value,
                                })
                              }
                              className="mt-1 w-full px-3 py-2 border border-emerald-200 rounded-lg bg-white"
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-6 rounded-xl border border-gray-200 p-4 bg-gray-50">
                  <label className="flex items-center gap-3 cursor-pointer mb-3">
                    <input
                      type="checkbox"
                      checked={formData.requiere_captura_facial}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          requiere_captura_facial: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-teal-500 rounded focus:ring-2 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700 font-medium">
                      Requerir captura facial
                    </span>
                  </label>

                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        Captura facial
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {editingUser
                          ? "Opcional al editar: puedes actualizar la biometría facial."
                          : formData.requiere_captura_facial
                            ? "Obligatorio al crear usuario para reconocimiento facial."
                            : "Opcional al crear usuario."}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={startFaceCamera}
                        disabled={faceCaptureBusy}
                        className="px-3 py-2 rounded-lg border border-teal-200 text-teal-700 bg-white hover:bg-teal-50 text-sm disabled:opacity-60"
                      >
                        <i className="ri-camera-line mr-1"></i>
                        Abrir cámara
                      </button>
                      {faceCaptureDataUrl && (
                        <button
                          type="button"
                          onClick={() => setFaceCaptureDataUrl("")}
                          className="px-3 py-2 rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-100 text-sm"
                        >
                          Limpiar
                        </button>
                      )}
                    </div>
                  </div>

                  {editingUser && (
                    <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-700">
                      <p className="font-medium text-gray-900">
                        Estado actual: {getBiometriaBadge(editingUser.id).label}
                      </p>
                      {editingBiometria?.updated_at && (
                        <p className="mt-1 text-gray-500">
                          Última actualización:{" "}
                          {new Date(editingBiometria.updated_at).toLocaleString(
                            "es-CL",
                          )}
                        </p>
                      )}
                    </div>
                  )}

                  {faceCaptureError && (
                    <div className="mt-3 text-sm text-red-600 flex items-center gap-2">
                      <i className="ri-error-warning-line"></i>
                      {faceCaptureError}
                    </div>
                  )}

                  {faceCaptureOpen && (
                    <div className="mt-4">
                      <div className="relative mx-auto aspect-[4/3] w-full max-w-2xl overflow-hidden rounded-xl bg-black shadow-inner">
                        <video
                          ref={videoRef}
                          className="absolute inset-0 h-full w-full object-cover -scale-x-100"
                          autoPlay
                          playsInline
                          muted
                          onClick={(event) =>
                            event.currentTarget.play().catch(() => {})
                          }
                        />
                        <div
                          className="pointer-events-none absolute inset-0"
                          aria-hidden="true"
                        >
                          <div className="absolute left-1/2 top-1/2 h-[78%] w-[46%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] border-[3px] border-white/95 shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]" />
                          <div className="absolute left-1/2 top-[9%] -translate-x-1/2 rounded-full bg-black/65 px-4 py-2 text-center text-xs font-semibold text-white shadow-sm">
                            Centra el rostro dentro del óvalo
                          </div>
                          <div className="absolute bottom-3 left-1/2 w-[90%] -translate-x-1/2 rounded-lg bg-black/65 px-3 py-2 text-center text-[11px] leading-4 text-white">
                            Mira de frente · Mantén buena iluminación · Evita
                            gorros, lentes oscuros y contraluz
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          onClick={captureFaceSnapshot}
                          className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 text-sm"
                        >
                          Capturar rostro
                        </button>
                        <button
                          type="button"
                          onClick={stopFaceCamera}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  {faceCaptureDataUrl && (
                    <div className="mt-4">
                      <p className="text-xs text-gray-600 mb-2">
                        Vista previa capturada
                      </p>
                      <img
                        src={faceCaptureDataUrl}
                        alt="Captura facial"
                        className="w-36 h-36 object-cover rounded-lg border border-gray-200"
                      />
                    </div>
                  )}
                </div>

                <div className="mt-6 space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.notificar_email}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          notificar_email: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-teal-500 rounded focus:ring-2 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">
                      Notificar por Email
                    </span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.notificar_whatsapp}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          notificar_whatsapp: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-teal-500 rounded focus:ring-2 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">
                      Notificar por WhatsApp
                    </span>
                  </label>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
                  >
                    {editingUser ? "Actualizar" : "Crear Usuario"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showPlanModal && selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  Asignar Plan a {selectedUser.nombre}
                </h2>
              </div>

              <div className="p-6">
                {planes.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-vip-crown-line text-5xl text-gray-300"></i>
                    <p className="text-gray-500 mt-4">
                      No hay planes activos disponibles
                    </p>
                    <p className="text-sm text-gray-400 mt-2">
                      Crea planes en la sección de Planes
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {planes.map((plan) => {
                      const colors = getColorClasses(plan.color || "teal");
                      return (
                        <div
                          key={plan.id}
                          className="border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors cursor-pointer"
                          onClick={() => handleAsignarPlan(plan)}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="text-xl font-bold text-gray-900">
                                {plan.nombre}
                              </h3>
                              <p className="text-sm text-gray-600 mt-1">
                                {plan.descripcion}
                              </p>
                            </div>
                            <div
                              className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center`}
                            >
                              <i
                                className={`ri-vip-crown-line text-2xl ${colors.icon}`}
                              ></i>
                            </div>
                          </div>
                          <div className="flex items-baseline gap-1 mb-4">
                            <span
                              className={`text-3xl font-bold ${colors.text}`}
                            >
                              ${plan.precio.toLocaleString("es-CL")}
                            </span>
                            <span className="text-gray-600">CLP</span>
                          </div>
                          <button className="w-full bg-teal-500 text-white py-2 rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap">
                            Seleccionar Plan
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="mt-6">
                  <button
                    onClick={() => {
                      setShowPlanModal(false);
                      setSelectedUser(null);
                    }}
                    className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
